// ============================================
// Care Plan Section
// Treatment goals and interventions
// ============================================

import React, { useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { FormSubmission, Problem, Goal, Intervention } from '../../types';
import FormField from '../ui/FormField';
import { v4 as uuidv4 } from 'uuid';
import { useLanguage } from '../../i18n/LanguageContext';

const t = (language: 'en' | 'es', en: string, es: string) => language === 'es' ? es : en;

const CarePlanSection: React.FC = () => {
  const { language } = useLanguage();
  const { register, control, formState: { errors }, setValue } = useFormContext<FormSubmission>();

  const { fields: problems, append: appendProblem, remove: removeProblem } = useFieldArray({
    control,
    name: 'carePlan.problems',
  });

  // Fixed provider name
  useEffect(() => {
    setValue('carePlan.ecmProvider.name', 'California Support Services');
  }, [setValue]);

  // Add one problem by default if none exist
  useEffect(() => {
    if (problems.length === 0) {
      addProblem();
    }
  }, []);

  const addProblem = () => {
    appendProblem({
      id: uuidv4(),
      problemName: '',
      description: '',
      priority: '',
      startDate: '',
      lastReviewed: '',
      resolvedReason: '',
      resolvedReasonOther: '',
      comments: '',
      goals: [{
        id: uuidv4(),
        goalName: '',
        description: '',
        startDate: '',
        priority: '',
        percentComplete: '',
        duration: '',
        closedDate: '',
        readinessToChange: '',
        closedReason: '',
        comments: '',
        outcome: '',
        targetDate: '',
        interventions: [{
          id: uuidv4(),
          interventionName: '',
          description: '',
          startDate: '',
          priority: '',
          targetDate: '',
          type: '',
          category: '',
          closedDate: '',
          effective: '',
          closedReason: '',
          comments: '',
        }],
      }],
    });
  };

  const handlePreview = () => {
    const formData = control._formValues as FormSubmission;
    const cp = formData.carePlan;

    if (!cp) {
      alert('No care plan data found');
      return;
    }

    const previewSrc = language === 'es' ? '/care_plan_es.html' : '/care_plan.html';
    const previewWindow = window.open(previewSrc, '_blank', 'width=1200,height=800');

    if (!previewWindow) {
      alert('Please allow popups to preview the care plan');
      return;
    }

    previewWindow.addEventListener('load', () => {
      const doc = previewWindow.document;

      const setFieldValue = (id: string, value: string) => {
        const element = doc.getElementById(id);
        if (element) {
          (element as any).value = value || '';
        }
      };

      const checkRadio = (name: string, value: string) => {
        if (!value) return;
        const radios = doc.getElementsByName(name);
        for (let i = 0; i < radios.length; i++) {
          const radio = radios[i] as HTMLInputElement;
          if (radio.value.toLowerCase() === value.toLowerCase()) {
            radio.checked = true;
            break;
          }
        }
      };

      setFieldValue('p1_ecm_provider_name', cp.ecmProvider?.name || '');
      setFieldValue('p1_ecm_provider_phone', cp.ecmProvider?.phone || '');
      setFieldValue('p1_lead_cm_name', cp.leadCareManager?.name || '');

      const problems = cp.problems || [];
      const problem1 = problems[0];

      if (problem1) {
        setFieldValue('p1_problem_1_name', problem1.problemName || '');
        setFieldValue('p1_problem_1_description', problem1.description || '');
        checkRadio('p1_prob1_priority', problem1.priority || '');

        const goals = problem1.goals || [];

        const goalConfigs = [
          {
            id: 1, page: 2, goalSuffix: '1_1', radioPrefix: 'g1', interventions: [
              { page: 'p2', num: 1, radioPrefix: 'i1', suffix: '1_1' },
              { page: 'p3', num: 2, radioPrefix: 'i2', suffix: '1_2' },
              { page: 'p3', num: 3, radioPrefix: 'i3', suffix: '1_3' },
              { page: 'p4', num: 4, radioPrefix: 'i4', suffix: '1_4' },
              { page: 'p4', num: 5, radioPrefix: 'i5', suffix: '1_5' }
            ]
          },
          {
            id: 2, page: 5, goalSuffix: '1_2', radioPrefix: 'g2', interventions: [
              { page: 'p5', num: 1, radioPrefix: 'i6', suffix: '2_1' },
              { page: 'p6', num: 2, radioPrefix: 'i7', suffix: '2_2' },
              { page: 'p6', num: 3, radioPrefix: 'i8', suffix: '2_3' },
              { page: 'p7', num: 4, radioPrefix: 'i9', suffix: '2_4' },
              { page: 'p7', num: 5, radioPrefix: 'i10', suffix: '2_5' }
            ]
          },
          {
            id: 3, page: 8, goalSuffix: '1_3', radioPrefix: 'g3', interventions: [
              { page: 'p8', num: 1, radioPrefix: 'i11', suffix: '3_1' },
              { page: 'p9', num: 2, radioPrefix: 'i12', suffix: '3_2' },
              { page: 'p9', num: 3, radioPrefix: 'i13', suffix: '3_3' },
              { page: 'p10', num: 4, radioPrefix: 'i14', suffix: '3_4' },
              { page: 'p10', num: 5, radioPrefix: 'i15', suffix: '3_5' }
            ]
          },
          {
            id: 4, page: 11, goalSuffix: '1_4', radioPrefix: 'g4', interventions: [
              { page: 'p11', num: 1, radioPrefix: 'i16', suffix: '4_1' },
              { page: 'p12', num: 2, radioPrefix: 'i17', suffix: '4_2' },
              { page: 'p12', num: 3, radioPrefix: 'i18', suffix: '4_3' },
              { page: 'p13', num: 4, radioPrefix: 'i19', suffix: '4_4' },
              { page: 'p13', num: 5, radioPrefix: 'i20', suffix: '4_5' }
            ]
          },
          {
            id: 5, page: 14, goalSuffix: '1_5', radioPrefix: 'g5', interventions: [
              { page: 'p14', num: 1, radioPrefix: 'i21', suffix: '5_1' },
              { page: 'p15', num: 2, radioPrefix: 'i22', suffix: '5_2' },
              { page: 'p15', num: 3, radioPrefix: 'i23', suffix: '5_3' },
              { page: 'p16', num: 4, radioPrefix: 'i24', suffix: '5_4' },
              { page: 'p16', num: 5, radioPrefix: 'i25', suffix: '5_5' }
            ]
          }
        ];

        const debugInfo: any[] = [];

        goals.forEach((goal: any, goalIndex: number) => {
          if (goalIndex < 5) {
            const config = goalConfigs[goalIndex];

            const goalPrefix = `p${config.page}_goal_${config.goalSuffix}`;
            setFieldValue(`${goalPrefix}_name`, goal.goalName || '');
            setFieldValue(`${goalPrefix}_description`, goal.description || '');
            setFieldValue(`${goalPrefix}_start_date`, goal.startDate || '');
            setFieldValue(`${goalPrefix}_percent_complete`, goal.percentComplete || '');
            setFieldValue(`${goalPrefix}_comments`, goal.comments || '');
            setFieldValue(`${goalPrefix}_outcome`, goal.outcome || '');

            checkRadio(`${config.radioPrefix}_priority`, goal.priority || '');
            checkRadio(`${config.radioPrefix}_duration`, goal.duration || '');
            checkRadio(`${config.radioPrefix}_rtc`, goal.readinessToChange || '');

            const interventions = goal.interventions || [];

            interventions.forEach((intervention: any, intIndex: number) => {
              if (intIndex < 5) {
                const intMapping = config.interventions[intIndex];

                const interventionDebug: any = {
                  goal: config.id,
                  num: intMapping.num,
                  name: intervention.interventionName,
                  fieldsFound: [],
                  fieldsNotFound: []
                };

                const setAndTrack = (fieldId: string, value: any): boolean => {
                  const element = doc.getElementById(fieldId);
                  if (element) {
                    (element as any).value = String(value || '');
                    interventionDebug.fieldsFound.push(fieldId);
                    return true;
                  } else {
                    interventionDebug.fieldsNotFound.push(fieldId);
                    return false;
                  }
                };

                setAndTrack(`${intMapping.page}_intervention_${intMapping.suffix}_name`, intervention.interventionName);
                setAndTrack(`${intMapping.page}_intervention_${intMapping.suffix}_description`, intervention.description);

                if (intIndex >= 2) {
                  setAndTrack(`${intMapping.page}_intervention_${intMapping.suffix}_start_date`, intervention.startDate);
                }

                setAndTrack(`${intMapping.page}_intervention_${intMapping.suffix}_target_date`, intervention.targetDate);

                if (!setAndTrack(`${intMapping.page}_intervention_${intMapping.suffix}_closed_date`, intervention.closedDate)) {
                  setAndTrack(`${intMapping.page}_intervention_${intMapping.suffix}_close_date`, intervention.closedDate);
                }

                setAndTrack(`${intMapping.page}_intervention_${intMapping.suffix}_comments`, intervention.comments);

                checkRadio(`${intMapping.radioPrefix}_priority`, intervention.priority || '');
                checkRadio(`${intMapping.radioPrefix}_type`, intervention.type || '');
                checkRadio(`${intMapping.radioPrefix}_cat`, intervention.category || '');
                checkRadio(`${intMapping.radioPrefix}_effective`, intervention.effective || '');

                // Handle Closed Reason Checkboxes
                if (intervention.closedReason) {
                  const reasonMap: Record<string, string> = {
                    'Changed': 'changed',
                    'Completed': 'completed',
                    'Member declined': 'declined',
                    'Member disenrolled': 'disenrolled',
                    'No longer achievable': 'no_longer',
                    'N/A': 'NA',
                    'Member transitioned to new provider': 'transitioned'
                  };

                  const slug = reasonMap[intervention.closedReason];
                  if (slug) {
                    const cbId = `${intMapping.page}_intervention_cb_${intMapping.suffix}_closed_reason_${slug}`;
                    const cb = doc.getElementById(cbId);
                    if (cb) {
                      (cb as HTMLInputElement).checked = true;
                      interventionDebug.fieldsFound.push(cbId);
                    } else {
                      interventionDebug.fieldsNotFound.push(cbId);
                    }
                  }
                }

                debugInfo.push(interventionDebug);
              }
            });
          }
        });

        const debugPanel = doc.createElement('div');
        debugPanel.style.cssText = 'position: fixed; bottom: 0; left: 0; right: 0; background: #333; color: #0f0; padding: 10px; font-family: monospace; font-size: 11px; max-height: 200px; overflow-y: auto; z-index: 10001; border-top: 2px solid #0f0;';

        let debugHtml = '<strong style="color: #fff;">🔍 DEBUG INFO - ALL GOALS:</strong><br>';
        debugInfo.forEach(info => {
          debugHtml += `<br><strong style="color: #ff0;">Goal ${info.goal} - Intervention ${info.num}: ${info.name}</strong><br>`;
          debugHtml += `✓ Found (${info.fieldsFound.length}): ${info.fieldsFound.join(', ')}<br>`;
          if (info.fieldsNotFound.length > 0) {
            debugHtml += `<span style="color: #f00;">✗ Not Found (${info.fieldsNotFound.length}): ${info.fieldsNotFound.join(', ')}</span><br>`;
          }
        });

        debugPanel.innerHTML = debugHtml;
        doc.body.appendChild(debugPanel);

        // Render additional problems (2-5) dynamically
        if (problems.length > 1) {
          const additionalProblems = problems.slice(1).map((prob: any, idx: number) => ({
            problemNumber: idx + 2,
            problemName: prob.problemName || '',
            description: prob.description || '',
            priority: prob.priority || '',
            startDate: prob.startDate || '',
            lastReviewed: prob.lastReviewed || '',
            resolvedReason: prob.resolvedReason || '',
            resolvedReasonOther: prob.resolvedReasonOther || '',
            comments: prob.comments || '',
            goals: (prob.goals || []).map((goal: any, gIdx: number) => ({
              goalNumber: gIdx + 1,
              goalName: goal.goalName || '',
              description: goal.description || '',
              startDate: goal.startDate || '',
              priority: goal.priority || '',
              percentComplete: goal.percentComplete || '',
              duration: goal.duration || '',
              closedDate: goal.closedDate || '',
              readinessToChange: goal.readinessToChange || '',
              closedReason: goal.closedReason || '',
              comments: goal.comments || '',
              outcome: goal.outcome || '',
              interventions: (goal.interventions || []).map((int: any, iIdx: number) => ({
                interventionNumber: iIdx + 1,
                interventionName: int.interventionName || '',
                description: int.description || '',
                startDate: int.startDate || '',
                targetDate: int.targetDate || '',
                priority: int.priority || '',
                type: int.type || '',
                category: int.category || '',
                closedDate: int.closedDate || '',
                effective: int.effective || '',
                closedReason: int.closedReason || '',
                comments: int.comments || '',
              }))
            }))
          }));

          if (typeof (previewWindow as any).renderAdditionalProblems === 'function') {
            (previewWindow as any).renderAdditionalProblems(additionalProblems);
          }
        }
      }
    });
  };


  const fillSampleData = () => {
    // Provider info
    setValue('carePlan.ecmProvider.name', 'ECM Provider Inc');
    setValue('carePlan.ecmProvider.phone', '555-0101');
    setValue('carePlan.leadCareManager.name', 'Jane Smith');
    setValue('carePlan.leadCareManager.phone', '555-0102');
    setValue('carePlan.leadCareManager.email', 'jane@ecm.com');

    // General information
    setValue('carePlan.ecmTargetGraduationDate', '2026-12-31');
    setValue('carePlan.postGraduationSupports', 'Member will transition to standard care management');
    setValue('carePlan.communitySupportsReferral', true);
    setValue('carePlan.communitySupportsDetails', 'Referred to local senior center and meal delivery program');
    setValue('carePlan.dsnpDualEligible', true);
    setValue('carePlan.dsnpHealthPlan', 'Medicare Advantage Plus');
    setValue('carePlan.memberComments', 'Member is engaged and motivated');
    setValue('carePlan.actualGraduationDate', '');

    // Create 5 goals with 5 interventions each
    const sampleProblem = {
      id: uuidv4(),
      problemName: 'Chronic Health Condition Management',
      description: 'Member requires comprehensive care coordination',
      priority: 'High',
      startDate: '2026-01-15',
      lastReviewed: '2026-02-01',
      resolvedReason: '',
      resolvedReasonOther: '',
      comments: 'Active monitoring required',
      goals: [
        {
          id: uuidv4(),
          goalName: 'Medication Adherence',
          description: 'Improve medication compliance to 95%',
          startDate: '2026-01-15',
          priority: 'High',
          percentComplete: '75',
          duration: 'Short Term',
          readinessToChange: 'Action',
          comments: 'Making good progress',
          outcome: 'Member taking medications regularly',
          interventions: [
            { id: uuidv4(), interventionName: 'Medication Management Support', description: 'Weekly medication review calls', startDate: '2026-01-15', priority: 'High', targetDate: '2026-03-15', type: 'Coordination', category: 'Medical', closedDate: '', closedReason: '', effective: 'Yes', comments: 'Member responsive to reminders' },
            { id: uuidv4(), interventionName: 'Weekly Wellness Check-in Calls', description: 'Monitor health status and medication adherence', startDate: '2026-01-15', priority: 'Medium', targetDate: '2026-06-15', type: 'Coordination', category: 'Medical', closedDate: '', closedReason: '', effective: 'Yes', comments: 'Calls scheduled every Monday' },
            { id: uuidv4(), interventionName: 'Transportation Assistance Coordination', description: 'Arrange rides to pharmacy and appointments', startDate: '2026-01-20', priority: 'Medium', targetDate: '2026-04-20', type: 'Coordination', category: 'Member', closedDate: '2026-03-15', closedReason: 'Completed', effective: 'Yes', comments: 'Rideshare service activated' },
            { id: uuidv4(), interventionName: 'Behavioral Health Counseling Referral', description: 'Connect with mental health services', startDate: '2026-01-25', priority: 'High', targetDate: '2026-05-25', type: 'Coordination', category: 'Behavioral', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Referral pending' },
            { id: uuidv4(), interventionName: 'Nutritional Counseling and Meal Planning', description: 'Dietary education for chronic condition', startDate: '2026-02-01', priority: 'Low', targetDate: '2026-07-01', type: 'Coordination', category: 'Medical', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Initial assessment scheduled' }
          ]
        },
        {
          id: uuidv4(),
          goalName: 'Regular Medical Appointments',
          description: 'Attend all scheduled medical appointments',
          startDate: '2026-01-15',
          priority: 'High',
          percentComplete: '60',
          duration: 'Long Term',
          readinessToChange: 'Preparation',
          comments: 'Some missed appointments',
          outcome: 'Improving attendance',
          interventions: [
            { id: uuidv4(), interventionName: 'Appointment Reminder System', description: 'Automated reminders 48hrs before appointments', startDate: '2026-01-15', priority: 'Medium', targetDate: '2026-12-31', type: 'Coordination', category: 'Member', closedDate: '', closedReason: '', effective: 'Yes', comments: 'SMS and email reminders active' },
            { id: uuidv4(), interventionName: 'Care Coordination with PCP', description: 'Regular communication with primary care provider', startDate: '2026-01-20', priority: 'High', targetDate: '2026-06-20', type: 'Coordination', category: 'Medical', closedDate: '2026-06-15', closedReason: 'Completed', effective: 'Yes', comments: 'Monthly PCP updates' },
            { id: uuidv4(), interventionName: 'Specialist Referral Management', description: 'Coordinate specialist appointments', startDate: '2026-01-25', priority: 'High', targetDate: '2026-05-25', type: 'Coordination', category: 'Medical', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Cardiology referral in progress' },
            { id: uuidv4(), interventionName: 'Medical Records Organization', description: 'Maintain updated medical history', startDate: '2026-02-01', priority: 'Low', targetDate: '2026-08-01', type: 'Coordination', category: 'Medical', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Digital records setup' },
            { id: uuidv4(), interventionName: 'Health Literacy Education', description: 'Teach member about their conditions', startDate: '2026-02-05', priority: 'Medium', targetDate: '2026-09-05', type: 'Coordination', category: 'Medical', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Educational materials provided' }
          ]
        },
        {
          id: uuidv4(),
          goalName: 'Social Support Network',
          description: 'Build strong support system',
          startDate: '2026-01-20',
          priority: 'Medium',
          percentComplete: '40',
          duration: 'Long Term',
          readinessToChange: 'Contemplation',
          comments: 'Member interested but hesitant',
          outcome: 'Exploring options',
          interventions: [
            { id: uuidv4(), interventionName: 'Support Group Referral', description: 'Connect with peer support groups', startDate: '2026-01-20', priority: 'Medium', targetDate: '2026-04-20', type: 'Coordination', category: 'Behavioral', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Virtual group options provided' },
            { id: uuidv4(), interventionName: 'Family Engagement Sessions', description: 'Include family in care planning', startDate: '2026-01-25', priority: 'Medium', targetDate: '2026-05-25', type: 'Coordination', category: 'Member', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Family meeting scheduled' },
            { id: uuidv4(), interventionName: 'Community Resource Connection', description: 'Link to local community services', startDate: '2026-02-01', priority: 'Low', targetDate: '2026-06-01', type: 'Coordination', category: 'Member', closedDate: '2026-05-30', closedReason: 'Member declined', effective: 'No', comments: 'Resource list compiled' },
            { id: uuidv4(), interventionName: 'Volunteer Companion Program', description: 'Match with friendly visitor', startDate: '2026-02-10', priority: 'Low', targetDate: '2026-07-10', type: 'Coordination', category: 'Member', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Application submitted' },
            { id: uuidv4(), interventionName: 'Technology Training for Communication', description: 'Teach video calling for family contact', startDate: '2026-02-15', priority: 'Low', targetDate: '2026-08-15', type: 'Coordination', category: 'Member', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Tablet provided' }
          ]
        },
        {
          id: uuidv4(),
          goalName: 'Financial Stability',
          description: 'Secure financial resources for healthcare',
          startDate: '2026-01-25',
          priority: 'High',
          percentComplete: '50',
          duration: 'Short Term',
          readinessToChange: 'Action',
          comments: 'Actively working on applications',
          outcome: 'Some benefits secured',
          interventions: [
            { id: uuidv4(), interventionName: 'Benefits Enrollment Assistance', description: 'Help apply for available programs', startDate: '2026-01-25', priority: 'High', targetDate: '2026-04-25', type: 'Coordination', category: 'Member', closedDate: '', closedReason: '', effective: 'Yes', comments: 'SNAP application approved' },
            { id: uuidv4(), interventionName: 'Prescription Assistance Program', description: 'Reduce medication costs', startDate: '2026-02-01', priority: 'High', targetDate: '2026-05-01', type: 'Coordination', category: 'Medical', closedDate: '', closedReason: '', effective: 'Yes', comments: 'Enrolled in manufacturer program' },
            { id: uuidv4(), interventionName: 'Utility Assistance Referral', description: 'Help with heating/cooling bills', startDate: '2026-02-05', priority: 'Medium', targetDate: '2026-06-05', type: 'Coordination', category: 'Member', closedDate: '2026-06-01', closedReason: 'Changed', effective: 'Yes', comments: 'LIHEAP application pending' },
            { id: uuidv4(), interventionName: 'Financial Counseling', description: 'Budget planning and debt management', startDate: '2026-02-10', priority: 'Medium', targetDate: '2026-07-10', type: 'Coordination', category: 'Member', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Referred to credit counselor' },
            { id: uuidv4(), interventionName: 'Medical Bill Negotiation', description: 'Advocate for payment plans', startDate: '2026-02-15', priority: 'Low', targetDate: '2026-08-15', type: 'Coordination', category: 'Member', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Hospital payment plan arranged' }
          ]
        },
        {
          id: uuidv4(),
          goalName: 'Housing Stability',
          description: 'Maintain safe and stable housing',
          startDate: '2026-02-01',
          priority: 'Critical',
          percentComplete: '30',
          duration: 'Long Term',
          readinessToChange: 'Preparation',
          comments: 'Urgent need for housing support',
          outcome: 'Exploring housing options',
          interventions: [
            { id: uuidv4(), interventionName: 'Housing Navigation Services', description: 'Connect with housing resources', startDate: '2026-02-01', priority: 'Critical', targetDate: '2026-05-01', type: 'Coordination', category: 'Member', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Housing navigator assigned' },
            { id: uuidv4(), interventionName: 'Rental Assistance Application', description: 'Apply for Section 8 and other programs', startDate: '2026-02-05', priority: 'Critical', targetDate: '2026-06-05', type: 'Coordination', category: 'Member', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Applications submitted' },
            { id: uuidv4(), interventionName: 'Home Safety Assessment', description: 'Evaluate current living conditions', startDate: '2026-02-10', priority: 'High', targetDate: '2026-07-10', type: 'Coordination', category: 'Member', closedDate: '', closedReason: '', effective: 'Unknown', comments: 'Assessment scheduled' },
            { id: uuidv4(), interventionName: 'Emergency Shelter Coordination', description: 'Backup plan if housing lost', startDate: '2026-02-15', priority: 'High', targetDate: '2026-08-15', type: 'Coordination', category: 'Member', closedDate: '2026-08-10', closedReason: 'No longer achievable', effective: 'N/A', comments: 'Emergency contacts established' },
            { id: uuidv4(), interventionName: 'Landlord Mediation', description: 'Resolve disputes with current landlord', startDate: '2026-02-20', priority: 'Medium', targetDate: '2026-09-20', type: 'Coordination', category: 'Member', closedDate: '2026-09-15', closedReason: 'Member transitioned to new provider', effective: 'Unknown', comments: 'Mediation session planned' }
          ]
        }
      ]
    };

    setValue('carePlan.problems', [sampleProblem] as any);
    alert('✅ Sample data filled! 5 goals with 25 interventions created. Click Preview to see all data.');
  };


  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">{t(language, 'Part 1 of 3: Provider Information', 'Parte 1 de 3: Informacion del Proveedor')}</h3>

        <div className="grid grid-cols-1 gap-6">
          <FormField label={t(language, 'Provider Name', 'Nombre del Proveedor')} required>
            <input
              type="text"
              value="California Support Services"
              readOnly
              className="form-input w-full rounded-md border-gray-300 bg-gray-100 cursor-not-allowed"
            />
            <input
              type="hidden"
              {...register('carePlan.ecmProvider.name')}
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label={t(language, 'Name', 'Nombre')} required>
              <input
                type="text"
                {...register('carePlan.leadCareManager.name', { required: t(language, 'Name is required', 'El nombre es obligatorio') })}
                className="form-input w-full rounded-md border-gray-300"
              />
            </FormField>

            <FormField label={t(language, 'Phone', 'Telefono')}>
              <input
                type="tel"
                {...register('carePlan.leadCareManager.phone')}
                className="form-input w-full rounded-md border-gray-300"
              />
            </FormField>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">{t(language, 'Part 2 of 3: General Information', 'Parte 2 de 3: Informacion General')}</h3>

        <div className="grid grid-cols-1 gap-6">
          <FormField label={t(language, 'ECM Target Graduation Date', 'Fecha Objetivo de Graduacion ECM')}>
            <input
              type="date"
              min="1900-01-01"
              {...register('carePlan.ecmTargetGraduationDate')}
              className="form-input w-full rounded-md border-gray-300"
            />
          </FormField>

          <FormField label={t(language, 'Post-Graduation Supports', 'Apoyos Post-Graduacion')}>
            <textarea
              {...register('carePlan.postGraduationSupports')}
              rows={2}
              className="form-textarea w-full rounded-md border-gray-300"
            />
          </FormField>

          <FormField label={t(language, 'Is this member being referred for community supports?', 'Se esta refiriendo a este miembro para apoyos comunitarios?')}>
            <select
              {...register('carePlan.communitySupportsReferral')}
              className="form-select w-full rounded-md border-gray-300"
            >
              <option value="">{t(language, 'Select', 'Seleccionar')}</option>
              <option value="true">{t(language, 'Yes', 'Si')}</option>
              <option value="false">No</option>
            </select>
          </FormField>

          <FormField label={t(language, 'Community Supports Details', 'Detalles de Apoyos Comunitarios')}>
            <textarea
              {...register('carePlan.communitySupportsDetails')}
              rows={2}
              className="form-textarea w-full rounded-md border-gray-300"
            />
          </FormField>

          <FormField label={t(language, 'D-SNP: Is this member dual eligible?', 'D-SNP: Es este miembro doblemente elegible?')}>
            <select
              {...register('carePlan.dsnpDualEligible')}
              className="form-select w-full rounded-md border-gray-300"
            >
              <option value="">{t(language, 'Select', 'Seleccionar')}</option>
              <option value="true">{t(language, 'Yes', 'Si')}</option>
              <option value="false">No</option>
            </select>
          </FormField>

          <FormField label={t(language, 'D-SNP Health Plan', 'Plan de Salud D-SNP')}>
            <input
              type="text"
              {...register('carePlan.dsnpHealthPlan')}
              className="form-input w-full rounded-md border-gray-300"
            />
          </FormField>

          <FormField label={t(language, 'Member Comments', 'Comentarios del Miembro')}>
            <textarea
              {...register('carePlan.memberComments')}
              rows={3}
              className="form-textarea w-full rounded-md border-gray-300"
            />
          </FormField>

          <FormField label={t(language, 'Actual Graduation Date', 'Fecha Real de Graduacion')}>
            <input
              type="date"
              min="1900-01-01"
              {...register('carePlan.actualGraduationDate')}
              className="form-input w-full rounded-md border-gray-300"
            />
          </FormField>
        </div>
      </div>

      {/* Part 3: Problems, Goals & Interventions */}
      <div>
        <div className="mb-4 flex justify-between items-start">
          <div>
            <h3 className="text-lg font-bold text-gray-900 border-b pb-2">{t(language, 'Part 3 of 3: Problems, Goals & Interventions', 'Parte 3 de 3: Problemas, Metas e Intervenciones')}</h3>
            <p className="text-sm text-gray-500">
              {t(language, 'Up to 5 problems, each with up to 5 goals, each goal with up to 5 interventions.', 'Hasta 5 problemas, cada uno con hasta 5 metas, cada meta con hasta 5 intervenciones.')}
            </p>
          </div>
          {problems.length < 5 && (
            <button
              type="button"
              onClick={addProblem}
              className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium whitespace-nowrap"
            >
              {t(language, '+ Add Problem', '+ Agregar Problema')}
            </button>
          )}
        </div>

        {problems.length === 0 && (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <p className="text-gray-500">{t(language, 'No problems added yet. Click "Add Problem" to begin.', 'No se han agregado problemas aun. Haga clic en "Agregar Problema" para comenzar.')}</p>
          </div>
        )}

        {problems.map((problem, problemIndex) => (
          <ProblemCard
            key={problem.id}
            problemIndex={problemIndex}
            totalProblems={problems.length}
            onRemove={() => removeProblem(problemIndex)}
          />
        ))}
      </div>
    </div>
  );
};



// Problem Card Component
const ProblemCard: React.FC<{
  problemIndex: number;
  totalProblems: number;
  onRemove: () => void;
}> = ({ problemIndex, totalProblems, onRemove }) => {
  const { language } = useLanguage();
  const { register, control } = useFormContext<FormSubmission>();

  const { fields: goals, append: appendGoal, remove: removeGoal } = useFieldArray({
    control,
    name: `carePlan.problems.${problemIndex}.goals` as const,
  });

  const addGoal = () => {
    appendGoal({
      id: uuidv4(),
      goalName: '',
      description: '',
      startDate: '',
      priority: '',
      percentComplete: '',
      duration: '',
      closedDate: '',
      readinessToChange: '',
      closedReason: '',
      comments: '',
      outcome: '',
      targetDate: '',
      interventions: [{
        id: uuidv4(),
        interventionName: '',
        description: '',
        startDate: '',
        priority: '',
        targetDate: '',
        type: '',
        category: '',
        closedDate: '',
        effective: '',
        closedReason: '',
        comments: '',
      }],
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
      <div className="flex justify-between items-center mb-4 pb-2 border-b">
        <h4 className="text-md font-bold text-gray-800">{t(language, 'Problem', 'Problema')} #{problemIndex + 1}</h4>
        {totalProblems > 1 && (
          <button
            type="button"
            onClick={onRemove}
            className="text-red-500 hover:text-red-700 text-sm font-medium"
          >
            {t(language, 'Remove Problem', 'Eliminar Problema')}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 mb-6">
        <FormField label={t(language, 'Problem Name', 'Nombre del Problema')}>
          <input
            type="text"
            {...register(`carePlan.problems.${problemIndex}.problemName` as const)}
            className="form-input w-full rounded-md border-gray-300"
            placeholder={t(language, 'e.g. Type 2 Diabetes', 'ej. Diabetes Tipo 2')}
          />
        </FormField>

        <FormField label={t(language, 'Priority', 'Prioridad')}>
          <div className="flex space-x-4">
            {([
              { value: 'Low', label: t(language, 'Low', 'Bajo') },
              { value: 'Medium', label: t(language, 'Medium', 'Medio') },
              { value: 'High', label: t(language, 'High', 'Alto') },
              { value: 'Critical', label: t(language, 'Critical', 'Critico') },
            ]).map((p) => (
              <label key={p.value} className="inline-flex items-center">
                <input
                  type="radio"
                  value={p.value}
                  {...register(`carePlan.problems.${problemIndex}.priority` as const)}
                />
                <span className="ml-2">{p.label}</span>
              </label>
            ))}
          </div>
        </FormField>

        <FormField label={t(language, 'Description', 'Descripcion')}>
          <textarea
            {...register(`carePlan.problems.${problemIndex}.description` as const)}
            rows={3}
            className="form-textarea w-full rounded-md border-gray-300"
            placeholder={t(language, 'Describe the problem...', 'Describa el problema...')}
          />
        </FormField>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={t(language, 'Start Date', 'Fecha de Inicio')}>
            <input
              type="date"
              min="1900-01-01"
              {...register(`carePlan.problems.${problemIndex}.startDate` as const)}
              className="form-input w-full rounded-md border-gray-300"
            />
          </FormField>

          <FormField label={t(language, 'Last Reviewed', 'Ultima Revision')}>
            <input
              type="date"
              min="1900-01-01"
              {...register(`carePlan.problems.${problemIndex}.lastReviewed` as const)}
              className="form-input w-full rounded-md border-gray-300"
            />
          </FormField>
        </div>

        <FormField label={t(language, 'Resolved Reason', 'Razon de Resolucion')}>
          <div className="space-y-2">
            {([
              { value: 'Member deceased', label: t(language, 'Member deceased', 'Miembro fallecido') },
              { value: 'Member transitioned to new provider', label: t(language, 'Member transitioned to new provider', 'Miembro transferido a nuevo proveedor') },
              { value: "Member's coverage terminated", label: t(language, "Member's coverage terminated", 'Cobertura del miembro terminada') },
            ]).map((reason) => (
              <label key={reason.value} className="flex items-center">
                <input
                  type="radio"
                  value={reason.value}
                  {...register(`carePlan.problems.${problemIndex}.resolvedReason` as const)}
                />
                <span className="ml-2 text-sm text-gray-700">{reason.label}</span>
              </label>
            ))}
            <label className="flex items-center">
              <input
                type="radio"
                value="Other"
                {...register(`carePlan.problems.${problemIndex}.resolvedReason` as const)}
              />
              <span className="ml-2 text-sm text-gray-700">{t(language, 'Other:', 'Otro:')}</span>
              <input
                type="text"
                {...register(`carePlan.problems.${problemIndex}.resolvedReasonOther` as const)}
                className="ml-2 form-input h-6 rounded-md border-gray-300 text-xs"
                placeholder={t(language, 'Specify', 'Especifique')}
              />
            </label>
          </div>
        </FormField>

        <FormField label={t(language, 'Comments', 'Comentarios')}>
          <textarea
            {...register(`carePlan.problems.${problemIndex}.comments` as const)}
            rows={2}
            className="form-textarea w-full rounded-md border-gray-300"
            placeholder={t(language, 'Additional comments...', 'Comentarios adicionales...')}
          />
        </FormField>
      </div>

      {/* Goals */}
      <div className="pl-4 border-l-4 border-blue-100">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h5 className="text-sm font-bold text-gray-700">{t(language, 'Goals (up to 5 per problem)', 'Metas (hasta 5 por problema)')}</h5>
            <p className="text-xs text-gray-500">{t(language, 'Each goal can have up to 5 interventions', 'Cada meta puede tener hasta 5 intervenciones')}</p>
          </div>
          {goals.length < 5 && (
            <button
              type="button"
              onClick={addGoal}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {t(language, '+ Add Goal', '+ Agregar Meta')}
            </button>
          )}
        </div>

        {goals.map((goal, goalIndex) => (
          <GoalCard
            key={goal.id}
            problemIndex={problemIndex}
            goalIndex={goalIndex}
            onRemove={() => removeGoal(goalIndex)}
          />
        ))}
      </div>
    </div>
  );
};

// Goal Card Component
const GoalCard: React.FC<{
  problemIndex: number;
  goalIndex: number;
  onRemove: () => void;
}> = ({ problemIndex, goalIndex, onRemove }) => {
  const { language } = useLanguage();
  const { register, control } = useFormContext<FormSubmission>();

  const { fields: interventions, append: appendIntervention, remove: removeIntervention } = useFieldArray({
    control,
    name: `carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions` as const,
  });

  const addIntervention = () => {
    appendIntervention({
      id: uuidv4(),
      interventionName: '',
      description: '',
      startDate: '',
      priority: '',
      targetDate: '',
      type: '',
      category: '',
      closedDate: '',
      effective: '',
      closedReason: '',
      comments: '',
    });
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-3">
      <div className="flex justify-between items-start mb-3">
        <span className="text-sm font-medium text-gray-600">{t(language, 'Goal', 'Meta')} #{goalIndex + 1}</span>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 text-xs"
        >
          {t(language, 'Remove', 'Eliminar')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <input
          {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.goalName` as const)}
          className="form-input w-full rounded-md border-gray-300 text-sm"
          placeholder={t(language, 'Goal name', 'Nombre de la meta')}
        />
        <select
          {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.priority` as const)}
          className="form-select rounded-md border-gray-300 text-sm"
        >
          <option value="">{t(language, 'Priority', 'Prioridad')}</option>
          <option value="Low">{t(language, 'Low', 'Bajo')}</option>
          <option value="Medium">{t(language, 'Medium', 'Medio')}</option>
          <option value="High">{t(language, 'High', 'Alto')}</option>
          <option value="Critical">{t(language, 'Critical', 'Critico')}</option>
        </select>
        <select
          {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.duration` as const)}
          className="form-select rounded-md border-gray-300 text-sm"
        >
          <option value="">{t(language, 'Duration', 'Duracion')}</option>
          <option value="Short Term">{t(language, 'Short Term', 'Corto Plazo')}</option>
          <option value="Long Term">{t(language, 'Long Term', 'Largo Plazo')}</option>
        </select>
      </div>

      <div className="mb-3">
        <textarea
          {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.description` as const)}
          className="form-textarea w-full rounded-md border-gray-300 text-sm"
          rows={2}
          placeholder={t(language, 'Goal description', 'Descripcion de la meta')}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <div>
          <label className="text-xs text-gray-500">{t(language, 'Start Date', 'Fecha de Inicio')}</label>
          <input
            type="date"
            min="1900-01-01"
            {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.startDate` as const)}
            className="form-input w-full rounded-md border-gray-300 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">{t(language, 'Closed Date', 'Fecha de Cierre')}</label>
          <input
            type="date"
            min="1900-01-01"
            {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.closedDate` as const)}
            className="form-input w-full rounded-md border-gray-300 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">{t(language, 'Percent Complete', '% Completado')}</label>
          <input
            type="text"
            {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.percentComplete` as const)}
            className="form-input w-full rounded-md border-gray-300 text-sm"
            placeholder="%"
          />
        </div>
      </div>

      <div className="mb-3">
        <label className="text-xs text-gray-500">{t(language, 'Readiness To Change', 'Disposicion al Cambio')}</label>
        <select
          {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.readinessToChange` as const)}
          className="form-select w-full rounded-md border-gray-300 text-sm"
        >
          <option value="">{t(language, 'Select', 'Seleccionar')}</option>
          <option value="Precontemplation">{t(language, 'Precontemplation', 'Precontemplacion')}</option>
          <option value="Contemplation">{t(language, 'Contemplation', 'Contemplacion')}</option>
          <option value="Preparation">{t(language, 'Preparation', 'Preparacion')}</option>
          <option value="Action">{t(language, 'Action', 'Accion')}</option>
          <option value="Maintenance">{t(language, 'Maintenance', 'Mantenimiento')}</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs text-gray-500">{t(language, 'Closed Reason', 'Razon de Cierre')}</label>
          <input
            type="text"
            {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.closedReason` as const)}
            className="form-input w-full rounded-md border-gray-300 text-sm"
            placeholder={t(language, 'Reason for closing...', 'Razon de cierre...')}
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">{t(language, 'Outcome', 'Resultado')}</label>
          <input
            type="text"
            {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.outcome` as const)}
            className="form-input w-full rounded-md border-gray-300 text-sm"
            placeholder={t(language, 'Goal outcome', 'Resultado de la meta')}
          />
        </div>
      </div>

      <div className="mb-3">
        <label className="text-xs text-gray-500">{t(language, 'Goal Comments', 'Comentarios de la Meta')}</label>
        <textarea
          {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.comments` as const)}
          className="form-textarea w-full rounded-md border-gray-300 text-sm"
          rows={2}
          placeholder={t(language, 'Additional comments about this goal...', 'Comentarios adicionales sobre esta meta...')}
        />
      </div>

      {/* Interventions */}
      <div className="pl-4 border-l-2 border-green-200">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-medium text-gray-500">{t(language, 'Interventions (up to 5 per goal)', 'Intervenciones (hasta 5 por meta)')}</span>
          {interventions.length < 5 && (
            <button
              type="button"
              onClick={addIntervention}
              className="text-green-600 hover:text-green-800 text-xs"
            >
              {t(language, '+ Add Intervention', '+ Agregar Intervencion')}
            </button>
          )}
        </div>

        {interventions.map((intervention, interventionIndex) => (
          <div key={intervention.id} className="bg-white border border-gray-200 rounded-lg p-3 mb-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-green-700">{t(language, 'Intervention', 'Intervencion')} #{interventionIndex + 1}</span>
              <button
                type="button"
                onClick={() => removeIntervention(interventionIndex)}
                className="text-red-400 hover:text-red-600 text-xs"
              >
                {t(language, 'Remove', 'Eliminar')}
              </button>
            </div>

            {/* Row 1: Name and Description */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <input
                {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions.${interventionIndex}.interventionName` as const)}
                className="form-input rounded-md border-gray-300 text-xs"
                placeholder={t(language, 'Intervention Name', 'Nombre de la Intervencion')}
              />
              <input
                {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions.${interventionIndex}.description` as const)}
                className="form-input rounded-md border-gray-300 text-xs"
                placeholder={t(language, 'Description', 'Descripcion')}
              />
            </div>

            {/* Row 2: Dates and Priority */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
              <div>
                <label className="text-xs text-gray-400">{t(language, 'Start Date', 'Fecha de Inicio')}</label>
                <input
                  type="date"
                  min="1900-01-01"
                  {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions.${interventionIndex}.startDate` as const)}
                  className="form-input w-full rounded-md border-gray-300 text-xs"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400">{t(language, 'Target Date', 'Fecha Objetivo')}</label>
                <input
                  type="date"
                  min="1900-01-01"
                  {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions.${interventionIndex}.targetDate` as const)}
                  className="form-input w-full rounded-md border-gray-300 text-xs"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400">{t(language, 'Priority', 'Prioridad')}</label>
                <select
                  {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions.${interventionIndex}.priority` as const)}
                  className="form-select w-full rounded-md border-gray-300 text-xs"
                >
                  <option value="">{t(language, 'Select', 'Seleccionar')}</option>
                  <option value="Low">{t(language, 'Low', 'Bajo')}</option>
                  <option value="Medium">{t(language, 'Medium', 'Medio')}</option>
                  <option value="High">{t(language, 'High', 'Alto')}</option>
                  <option value="Critical">{t(language, 'Critical', 'Critico')}</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400">{t(language, 'Closed Date', 'Fecha de Cierre')}</label>
                <input
                  type="date"
                  min="1900-01-01"
                  {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions.${interventionIndex}.closedDate` as const)}
                  className="form-input w-full rounded-md border-gray-300 text-xs"
                />
              </div>
            </div>

            {/* Row 3: Type, Category, Effective */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
              <div>
                <label className="text-xs text-gray-400">{t(language, 'Type', 'Tipo')}</label>
                <select
                  {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions.${interventionIndex}.type` as const)}
                  className="form-select w-full rounded-md border-gray-300 text-xs"
                >
                  <option value="">{t(language, 'Select', 'Seleccionar')}</option>
                  <option value="Coordination">{t(language, 'Coordination', 'Coordinacion')}</option>
                  <option value="Graduation">{t(language, 'Graduation', 'Graduacion')}</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400">{t(language, 'Category', 'Categoria')}</label>
                <select
                  {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions.${interventionIndex}.category` as const)}
                  className="form-select w-full rounded-md border-gray-300 text-xs"
                >
                  <option value="">{t(language, 'Select', 'Seleccionar')}</option>
                  <option value="Behavioral">{t(language, 'Behavioral', 'Conductual')}</option>
                  <option value="Medical">{t(language, 'Medical', 'Medico')}</option>
                  <option value="Member">{t(language, 'Member', 'Miembro')}</option>
                  <option value="Other">{t(language, 'Other', 'Otro')}</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400">{t(language, 'Effective?', 'Efectiva?')}</label>
                <select
                  {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions.${interventionIndex}.effective` as const)}
                  className="form-select w-full rounded-md border-gray-300 text-xs"
                >
                  <option value="">{t(language, 'Select', 'Seleccionar')}</option>
                  <option value="Yes">{t(language, 'Yes', 'Si')}</option>
                  <option value="No">No</option>
                  <option value="Unknown">{t(language, 'Unknown', 'Desconocido')}</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400">{t(language, 'Closed Reason', 'Razon de Cierre')}</label>
                <select
                  {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions.${interventionIndex}.closedReason` as const)}
                  className="form-select w-full rounded-md border-gray-300 text-xs"
                >
                  <option value="">{t(language, 'Select', 'Seleccionar')}</option>
                  <option value="Changed">{t(language, 'Changed', 'Cambiado')}</option>
                  <option value="Completed">{t(language, 'Completed', 'Completado')}</option>
                  <option value="Member declined">{t(language, 'Member declined', 'Miembro rechazo')}</option>
                  <option value="Member disenrolled">{t(language, 'Member disenrolled', 'Miembro dado de baja')}</option>
                  <option value="No longer achievable">{t(language, 'No longer achievable', 'Ya no es alcanzable')}</option>
                  <option value="N/A">N/A</option>
                  <option value="Member transitioned to new provider">{t(language, 'Member transitioned to new provider', 'Miembro transferido a nuevo proveedor')}</option>
                </select>
              </div>
            </div>

            {/* Row 4: Comments */}
            <div className="mb-2">
              <label className="text-xs text-gray-400">{t(language, 'Comments', 'Comentarios')}</label>
              <textarea
                {...register(`carePlan.problems.${problemIndex}.goals.${goalIndex}.interventions.${interventionIndex}.comments` as const)}
                className="form-textarea w-full rounded-md border-gray-300 text-xs"
                rows={2}
                placeholder={t(language, 'Additional comments about this intervention...', 'Comentarios adicionales sobre esta intervencion...')}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};



export default CarePlanSection;



